import type { ReactNode } from "react"

import { useTranslation } from "react-i18next"

import type { GraphMeeting, HomeContextResponse } from "@/api/home-context"
import type { ChatHomeSignals } from "@/types/user-settings"

import { CalendarCheck } from "@/components/icons"
import { Card } from "@/components/ui/card"

import {
  InboxSection,
  ProjectPulseSection,
  RightNowBlock,
  TodayStackSection,
  TomorrowSection,
  WaitingSection,
} from "./day-brief-sections"

function DayBriefHeader() {
  const { t } = useTranslation()
  return (
    <div className="flex items-center justify-between gap-3" data-testid="day-brief-header">
      <p className="text-muted-foreground text-[11px] font-semibold tracking-wider uppercase">
        {t("chatHome.dayBrief.headerTitle", { defaultValue: "Today's brief" })}
      </p>
    </div>
  )
}

type DayBriefProps = {
  personalization: HomeContextResponse
  enabledSignals: ChatHomeSignals
  compact?: boolean
  onMeetingPrep?: (meeting: GraphMeeting) => void
  onPromptClick: (prompt: string) => void
}

export function DayBrief({
  personalization,
  enabledSignals,
  compact = false,
  onMeetingPrep,
  onPromptClick,
}: DayBriefProps) {
  const nextMeeting = personalization.graph.next_meeting
  const todayMeetings = personalization.graph.today_meetings ?? []
  const upcoming = personalization.graph.upcoming_meetings
  const tomorrowMeetings = personalization.graph.tomorrow_meetings ?? []
  const highlights = personalization.graph.unread_mail?.highlights ?? []
  const projectPulse = personalization.helloai.project_pulse ?? null
  const waitingOnYou = personalization.helloai.waiting_on_you ?? []

  // "Senare idag" stack: meetings after the right-now block. Empty when there's
  // no next meeting (the empty-day state takes over below).
  const stackMeetings = nextMeeting
    ? upcoming.filter((m) => m.id !== nextMeeting.id).slice(0, 2)
    : []

  const hasNextMeeting = nextMeeting !== null && Boolean(nextMeeting.start_iso)
  const showInbox = enabledSignals.dayBriefInbox !== false && highlights.length > 0
  const showPulse = enabledSignals.dayBriefProjectPulse !== false && projectPulse !== null
  const showWaiting = enabledSignals.dayBriefWaitingOnYou !== false && waitingOnYou.length > 0
  const showTomorrow = enabledSignals.dayBriefTomorrow !== false && tomorrowMeetings.length > 0

  if (!hasNextMeeting && !showInbox && !showPulse && !showWaiting && !showTomorrow) {
    return <DayBriefEmpty compact={compact} todayMeetingCount={todayMeetings.length} />
  }

  // Collect mid-card sections in render order, then group into pairs so they
  // flow as 2-column rows on the wide layout (single column when compact).
  const middleSections: ReactNode[] = []
  if (hasNextMeeting && stackMeetings.length > 0) {
    middleSections.push(
      <TodayStackSection key="today-stack" meetings={stackMeetings} onMeetingPrep={onMeetingPrep} />
    )
  }
  if (showInbox) {
    middleSections.push(
      <InboxSection key="inbox" highlights={highlights} onPromptClick={onPromptClick} />
    )
  }
  if (showPulse) {
    middleSections.push(
      // showPulse already implies projectPulse is non-null (see derivation above)
      <ProjectPulseSection key="pulse" pulse={projectPulse!} onPromptClick={onPromptClick} />
    )
  }
  if (showWaiting) {
    middleSections.push(
      <WaitingSection key="waiting" threads={waitingOnYou} onPromptClick={onPromptClick} />
    )
  }
  const sectionPairs: ReactNode[][] = []
  for (let i = 0; i < middleSections.length; i += 2) {
    sectionPairs.push(middleSections.slice(i, i + 2))
  }

  const cardClassName = compact
    ? "border-primary/30 bg-primary/5 mx-auto flex w-full max-w-2xl flex-col gap-6 border-2 px-7 py-6 shadow-md"
    : "border-primary/30 bg-primary/5 mx-auto flex w-full max-w-4xl flex-col gap-7 border-2 px-10 py-7 shadow-md"
  const twoColGridClass = compact
    ? "border-foreground/10 grid gap-y-6 border-t-2 pt-6"
    : "border-foreground/10 grid gap-x-10 gap-y-7 border-t-2 pt-7 sm:grid-cols-2"

  return (
    <Card
      className={cardClassName}
      data-meeting-density={compact ? "compact" : "regular"}
      data-testid="day-brief"
    >
      <DayBriefHeader />
      {hasNextMeeting ? (
        // hasNextMeeting derived from `nextMeeting !== null && start_iso` above
        <RightNowBlock compact={compact} meeting={nextMeeting!} onPrep={onMeetingPrep} />
      ) : null}

      {sectionPairs.map((pair, idx) => (
        <div key={idx} className={twoColGridClass}>
          {pair}
        </div>
      ))}

      {showTomorrow ? <TomorrowSection compact={compact} meetings={tomorrowMeetings} /> : null}
    </Card>
  )
}

function DayBriefEmpty({
  compact,
  todayMeetingCount,
}: {
  compact: boolean
  todayMeetingCount: number
}) {
  const { t } = useTranslation()
  const cardClassName = compact
    ? "border-primary/20 bg-primary/5 mx-auto flex w-full max-w-2xl flex-col gap-2 border px-5 py-4 shadow-sm"
    : "border-primary/20 bg-primary/5 mx-auto flex min-h-[112px] w-full max-w-2xl flex-col gap-3 border px-6 py-5 shadow-sm"
  // 0 today meetings reads as an open day; > 0 means the day had meetings and is
  // now wrapped — the existing localized "no more meetings" copy fits there.
  const title =
    todayMeetingCount === 0
      ? t("chatHome.dayBrief.openDayTitle", { defaultValue: "Nothing pressing today." })
      : t("chatHome.meetingHero.doneTitle", "No more meetings for the day.")
  return (
    <Card className={cardClassName} data-testid="day-brief-empty">
      <DayBriefHeader />
      <div className="flex items-start gap-3">
        <CalendarCheck className="text-primary mt-0.5 size-5 shrink-0" />
        <div className="flex-1 space-y-1">
          <p className="text-foreground/80 text-xs font-medium tracking-wide uppercase">
            {t("chatHome.meetingHero.doneLabel", "Meetings")}
          </p>
          <h2 className="text-lg leading-tight font-semibold">{title}</h2>
          <p className="text-muted-foreground text-sm">
            {t("chatHome.meetingHero.doneVariation", "You have room to focus.")}
          </p>
        </div>
      </div>
    </Card>
  )
}
